import { removeCookie, setCookie } from "@/features/shared/cookies/cookie.store";
import { getCookie } from "@/features/shared/cookies/cookie.utils";
import appConfigs from "virtual:app-configs";

const APP_CONFIG_VARIABLES = [
  "VITE_SENTRY_ORG",
  "VITE_SENTRY_PROJECT",
  "VITE_SENTRY_DSN",
  "VITE_CLICKNPARK_API_URL",
  "VITE_CENTRAL_API_URL",
  "VITE_CENTRAL_API_SERVICE",
  "VITE_CLICKNPARK_WEB_APP_URL",
  "VITE_CLICKNPARK_AUTH_APP_URL",
  "VITE_CLICKNPARK_CHECKOUT_APP_URL",
  "VITE_CLICKNPARK_DASHBOARD_APP_URL",
  "VITE_CLICKNPARK_CENTRAL_APP_URL",
  "VITE_JANUS_SERVER_URL",
  "VITE_STAFF_FIREBASE_API_KEY",
  "VITE_STAFF_FIREBASE_AUTH_DOMAIN",
  "VITE_STAFF_FIREBASE_PROJECT_ID",
  "VITE_STAFF_FIREBASE_APP_ID",
] as const;


export type AppConfigVariable = (typeof APP_CONFIG_VARIABLES)[number];
export type AppConfig = Record<AppConfigVariable, string>;
export type AppConfigKey =
  | "development"
  | "staging"
  | "production"
  | "local:development"
  | "local:staging"
  | "local:production";

// Constants
const ENV_COOKIE_KEY = "cnp_app_config_key";
const INSTANCE_TEMPORARY_ID_COOKIE_KEY = "cnp_instance_temporary_id";
const LOCAL_APP_CONFIG_KEYS: AppConfigKey[] = ["local:development", "local:staging", "local:production"];
const VALID_ENVIRONMENTS: AppConfigKey[] = [
  "development",
  "staging",
  "production",
  ...(appConfigs?.["local:development"] ? LOCAL_APP_CONFIG_KEYS : []),
];
const INITIAL_APP_CONFIG_KEY = import.meta.env.VITE_INITIAL_APP_CONFIG_KEY as AppConfigKey;

// Utility functions
const getAppConfigs = () => appConfigs;
const isValidAppConfigKey = (key: AppConfigKey): boolean => VALID_ENVIRONMENTS.includes(key);
const isValidAppConfig = (): boolean => APP_CONFIG_VARIABLES.every((v) => getAppConfig()[v]);
const getMissingAppConfigKeys = (): string[] => APP_CONFIG_VARIABLES.filter((v) => !getAppConfig()[v]);

/**
 * Logs the app config by key
 * @param key - The app config key
 */
function logAppConfig(key: AppConfigKey): void {
  const configs = getAppConfigs();
  // `virtual:app-configs` already merges base + .env.local for `local:*` keys
  const config = configs[key];
  if (key.startsWith("local:")) {
    console.log(`⚙️ Loaded local with ${key} as base config`);
  } else {
    console.log(`⚙️ Loaded app config ${key}`);
  }

  console.table(config);
}

/**
 * Validates the app config by checking if all the required environment variables are set
 * @throws An error if the app config is missing
 */
function validateAppConfig(): void {
  if (!isValidAppConfig()) {
    throw new Error(
      `Missing app config.\nMake sure you have the following environment variables set in your .env.local file or in the corresponding config file ./config/*.env:\n\n${getMissingAppConfigKeys()
        .map((v) => `${v}`)
        .join("\n")}`,
    );
  }
}


/**
 * Initializes the app config by setting the current app config key from the environment variable
 * @returns The current app config key
 */
function initializeAppConfig(): void {
  // Production deployed apps
  // ------------------------------------------------------------

  if (!isSandbox()) {
    setCookie(ENV_COOKIE_KEY, "production");
    return;
  }

  // Sandbox deployed apps
  // ------------------------------------------------------------

  const appConfigKey = getCookie<AppConfigKey>(ENV_COOKIE_KEY);

  if (!isDevMode()) {
    if (appConfigKey && isValidAppConfigKey(appConfigKey)) {
      setCookie(ENV_COOKIE_KEY, appConfigKey);
      validateAppConfig();
      logAppConfig(appConfigKey);
      return;
    }

    setCookie(ENV_COOKIE_KEY, "development");
    logAppConfig("development");

    return;
  }

  // Local development
  // ------------------------------------------------------------

  // Check if the user has restarted the app
  const instanceTemporaryId = import.meta.env.VITE_INSTANCE_TEMPORARY_ID;
  const isFreshInstance = getCookie(INSTANCE_TEMPORARY_ID_COOKIE_KEY) !== instanceTemporaryId;

  if (!isFreshInstance) {
    // Is there already a app config key set?
    const appConfigKey = getCookie<AppConfigKey>(ENV_COOKIE_KEY);
    if (appConfigKey && isValidAppConfigKey(appConfigKey)) {
      setCookie(ENV_COOKIE_KEY, appConfigKey);
      validateAppConfig();
      logAppConfig(appConfigKey);
      return;
    }
  }

  if (isFreshInstance) {
    // Set the instance temporary id cookie
    setCookie(INSTANCE_TEMPORARY_ID_COOKIE_KEY, instanceTemporaryId);
  }

  // If the initial app config key is valid, set it as the current app config key
  if (INITIAL_APP_CONFIG_KEY && isValidAppConfigKey(INITIAL_APP_CONFIG_KEY)) {
    setCookie(ENV_COOKIE_KEY, INITIAL_APP_CONFIG_KEY);
    validateAppConfig();
    logAppConfig(INITIAL_APP_CONFIG_KEY);
  }
}

/**
 * Checks if the current environment is a sandbox environment
 * @returns True if the current environment is a sandbox environment, false otherwise
 */
function isSandbox(): boolean {
  return import.meta.env.VITE_SANDBOX === "true";
}

/**
 * Checks if the current environment is a development environment
 * @returns True if the current environment is a development environment, false otherwise
 */
function isDevMode(): boolean {
  return import.meta.env.MODE === "development";
}

/**
 * Lists the app config keys that have been defined
 * @returns The app config keys
 */
function listAppConfigKeys(): AppConfigKey[] {
  return VALID_ENVIRONMENTS;
}

/**
 * Gets the current app config key from the cookie or returns the default environment
 * @returns The current app config key
 */
function getAppConfigKey(): AppConfigKey {
  // Production is the default environment when not in sandbox
  if (!isSandbox()) return "production";
  // Get the stored environment from the cookie
  const stored = getCookie<AppConfigKey>(ENV_COOKIE_KEY);
  // If the stored environment is valid, return it
  if (stored && isValidAppConfigKey(stored)) return stored;
  // Return the initial app config key from the environment variable (comes from scripts/dev.mjs)
  if (isDevMode() && INITIAL_APP_CONFIG_KEY) return INITIAL_APP_CONFIG_KEY;
  // Return the development environment as default
  return "development";
}

/**
 * Sets the app config key in the cookie and deletes the CIP token cookies (which need to be refreshed completely)
 */
function setAppConfigKey(key: AppConfigKey): void {
  setCookie(ENV_COOKIE_KEY, key);
  removeCookie("cnp_cip_token");
  removeCookie("cnp_cip_token_expiry");
}

/**
 * Gets the app config from the manifest for the current app config key
 */
function getAppConfig(): AppConfig {
  const configs = getAppConfigs();
  const key = getAppConfigKey();
  // `local:*` entries are already base + .env.local (see vite-app-configs.plugin.ts)
  return configs[key] as AppConfig;
}

export { getAppConfig, getAppConfigKey, initializeAppConfig, isDevMode, isSandbox, listAppConfigKeys, setAppConfigKey };

